/**
 * A bundle's book record (part 24c, P1): the bundle's metadata and theme as stored when part 05 reads them, so a book comes
 * back exactly (L10), else a refused book record's (P11), else new ones; the title the writer gave over the metadata's only
 * when it differs. Pure.
 */
import { DEFAULT_THEME, metadataFromJson, newMetadata, serializeMetadata, serializeTheme, themeFromJson } from '@cockatrice/layout';
import type { BookMetadata } from '@cockatrice/layout';
import type { BookTexts } from '@cockatrice/export/bundle';
import type { BookRecord } from '@cockatrice/storage';
import { cleanTitle } from '../shell/workspace.ts';

/** The first of `texts` that `read` takes, with what it reads as; null when none does. */
function firstRead<T>(texts: readonly string[], read: (text: string) => T): { readonly text: string; readonly value: T } | null {
  for (const text of texts) {
    try {
      return { text, value: read(text) };
    } catch {
      // P1: one part 05 refuses is passed over.
    }
  }
  return null;
}

/** The book's title as the review shows it: the metadata's when part 05 reads it, else `fallback` (the file's name). */
export function bundleTitle(metadata: string | null, fallback: string): string {
  return firstRead(metadata === null ? [] : [metadata], metadataFromJson)?.value.title ?? fallback;
}

/**
 * P1: book `bookId`'s record, made at `now`: the first metadata of `texts` part 05 reads (else new metadata titled
 * `untitled`), its text kept as stored unless `title` (cleaned as part 05 does titles; empty keeps it) differs from its
 * title; the first theme part 05 reads, as stored, else the default.
 */
export function bundleBook(bookId: string, texts: BookTexts, title: string, untitled: string, now: number): BookRecord {
  const metadata = firstRead(texts.metadata, metadataFromJson);
  const theme = firstRead(texts.theme, themeFromJson);
  const wanted = cleanTitle(title);
  const base: BookMetadata = metadata?.value ?? newMetadata(cleanTitle(untitled));
  const text = metadata !== null && (wanted === '' || wanted === base.title) ? metadata.text : serializeMetadata({ ...base, title: wanted === '' ? base.title : wanted });
  return { id: bookId, metadata: text, theme: theme?.text ?? serializeTheme(DEFAULT_THEME), createdAt: now };
}
